// Pure helpers for the Override Lookup Simulator card (debug which overrides
// apply for hypothetical inputs, mirroring how a real order resolves).
//
// The /api_v2/overrides/lookup endpoint takes query params: product_ids
// (required) + at least one scope (branch, panelist/any, or state), plus
// optional county/zip (FIPS) and data_types. It expands a passed branch into
// its ancestry server-side, so we send the single selected branch as-is.

// The endpoint requires a product AND at least one scope to return anything.
export const isLookupValid = ({ productIds, branchIds, panelistId, anyPanelists, state }) =>
  !!(productIds && productIds.length) &&
  !!((branchIds && branchIds.length) || panelistId || anyPanelists || state)

// Assemble the GET query string. Only present fields are included. state/county
// are FIPS codes (resolved by the caller from the state/county mapping).
export const buildLookupQuery = ({
  productIds,
  branchIds,
  panelistId,
  anyPanelists,
  state,
  county,
  zip,
  dataTypes,
}) => {
  const parts = []
  if (productIds && productIds.length) { parts.push(`product_ids=${productIds.join(',')}`) }
  if (branchIds && branchIds.length) { parts.push(`branch_hierarchy=${branchIds.join(',')}`) }
  if (panelistId) { parts.push(`panelist_id=${panelistId}`) }
  if (anyPanelists) { parts.push('any_panelists=true') }
  if (state) { parts.push(`state=${state}`) }
  if (county) { parts.push(`county=${county}`) }
  if (zip) { parts.push(`zip=${encodeURIComponent(zip)}`) }
  if (dataTypes && dataTypes.length) { parts.push(`data_types=${dataTypes.join(',')}`) }
  return parts.join('&')
}

// Shape the lookup response into per-product display rows. The first override
// per product is the "winner" (engine returns them in priority order). Joins
// sheet_id -> sheet description, and branch ids -> names for readability.
export const shapeLookupResults = (response, mappingData = {}) => {
  if (!response || !response.products) {
    return []
  }
  const sheetsById = {}
  ;(response.sheet_priority || []).forEach((s) => {
    sheetsById[String(s.id)] = s
  })
  const nameOf = (list, idKey, nameKey, id) => {
    const match = (list || []).find((item) => String(item[idKey]) === String(id))
    return match ? match[nameKey] : String(id)
  }

  return Object.entries(response.products).map(([productId, overrides]) => ({
    productId,
    productName: nameOf(mappingData.products, 'id', 'description', productId),
    overrides: (overrides || []).map((o, index) => ({
      id: String(o.id),
      isWinner: index === 0,
      overrideData: o.override_data || {},
      sheetName: sheetsById[String(o.sheet_id)] ? sheetsById[String(o.sheet_id)].description : 'Unknown sheet',
      branchNames: (o.branch_id || []).map((bid) => nameOf(mappingData.branches, 'id', 'name', bid)),
      locationType: o.location_type || null,
      states: o.states || [],
      counties: o.counties || [],
      zips: o.zips || [],
    })),
  }))
}
